"use client";

import { Calendar, User, ArrowRight, ArrowLeft } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="h-12 w-3/4 bg-muted/40 animate-pulse rounded-lg" />
        <div className="h-[400px] w-full bg-muted/40 animate-pulse rounded-lg" />
        <div className="space-y-3">
          <div className="h-4 w-full bg-muted/40 animate-pulse rounded" />
          <div className="h-4 w-full bg-muted/40 animate-pulse rounded" />
          <div className="h-4 w-2/3 bg-muted/40 animate-pulse rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Unable to load the campaign story.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Campaign not found.</p>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "Vivid Studio Team";
  const authorAvatar = post.author?.avatar?.["96"] || post.author?.avatar?.["48"];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="font-default">
      {}
      <div className="mb-10">
        <Link
          to="/OurWorkPage"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="size-4" />
          Back to our portfolio
        </Link>
      </div>

      {}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-12">
        <div className="lg:col-span-8 lg:col-start-2">
          {post.categories && post.categories.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {post.categories.slice(0, 3).map((cat, i) => (
                <span
                  key={cat.id}
                  data-index={i}
                  className="text-xs uppercase tracking-widest text-primary font-default font-bold"
                >
                  {cat.name}
                  {i < Math.min(post.categories!.length, 3) - 1 && (
                    <span className="ml-2 text-muted-foreground">·</span>
                  )}
                </span>
              ))}
            </div>
          )}

          <h1
            className="font-default text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground font-bold"
            dangerouslySetInnerHTML={{ __html: post.title }}
          />

          <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-3">
              <Avatar className="size-10 rounded-lg">
                {authorAvatar ? (
                  <AvatarImage src={authorAvatar} alt={authorName} />
                ) : null}
                <AvatarFallback className="rounded-lg bg-muted text-foreground text-xs">
                  {authorName.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-foreground font-medium">{authorName}</p>
                <p className="text-xs text-muted-foreground">Creative Lead</p>
              </div>
            </div>

            {formattedDate && (
              <>
                <Separator orientation="vertical" className="h-10 hidden sm:block" />
                <div className="flex items-center gap-2">
                  <Calendar className="size-4" />
                  <span>{formattedDate}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {}
      {post.featuredImage && (
        <div className="mb-16 lg:mb-20">
          <div className="overflow-hidden rounded-lg bg-muted shadow-lg">
            <img
              src={post.featuredImage}
              alt={post.title}
              loading="lazy"
              decoding="async"
              className="w-full h-auto object-cover aspect-[16/9]"
            />
          </div>
        </div>
      )}

      {}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        {}
        {post.excerpt && (
          <aside className="lg:col-span-3 lg:col-start-1">
            <div className="lg:sticky lg:top-24">
              <p className="text-xs uppercase tracking-widest text-primary font-bold mb-3">
                Project Brief
              </p>
              <div
                className="font-default text-lg italic text-foreground/80 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: post.excerpt }}
              />
            </div>
          </aside>
        )}

        {}
        <div className={`${post.excerpt ? "lg:col-span-8 lg:col-start-5" : "lg:col-span-8 lg:col-start-3"}`}>
          <div
            className="prose prose-lg max-w-none font-default text-foreground/90 leading-relaxed [&_p]:mb-6 [&_p]:text-base [&_p]:sm:text-lg [&_h2]:font-default [&_h2]:font-bold [&_h2]:text-3xl [&_h2]:mt-12 [&_h2]:mb-6 [&_h2]:text-foreground [&_h3]:font-default [&_h3]:font-bold [&_h3]:text-2xl [&_h3]:mt-10 [&_h3]:mb-4 [&_h3]:text-foreground [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_img]:rounded-lg [&_img]:my-8 [&_blockquote]:border-l-2 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:font-default [&_blockquote]:text-xl [&_blockquote]:my-8 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_li]:mb-2"
            dangerouslySetInnerHTML={{ __html: post.content || "" }}
          />

          {}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-border">
              <p className="text-xs uppercase tracking-widest text-primary font-bold mb-4">
                Capabilities
              </p>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, i) => (
                  <span
                    key={tag.id}
                    data-index={i}
                    className="inline-flex items-center px-3 py-1 rounded-lg bg-muted text-sm text-foreground/80"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {}
      <div className="mt-20 lg:mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-10 lg:col-start-2">
            <Card className="bg-card shadow-lg rounded-lg border-border">
              <CardContent className="p-8 sm:p-10">
                <div className="flex flex-col sm:flex-row gap-6 items-start">
                  <Avatar className="size-20 rounded-lg shrink-0">
                    {authorAvatar ? (
                      <AvatarImage src={authorAvatar} alt={authorName} />
                    ) : null}
                    <AvatarFallback className="rounded-lg bg-muted text-foreground">
                      <User className="size-8" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-widest text-primary font-bold mb-2">
                      About the Creator
                    </p>
                    <h3 className="font-default text-2xl text-foreground font-bold mb-3">
                      {authorName}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      A lead creative at Vivid Studio, specializing in high-impact brand storytelling, 
                      cinematic video production, and strategic campaign development for forward-thinking brands.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="space-y-3">
            <div className="h-48 bg-muted/40 animate-pulse rounded-lg" />
            <div className="h-4 w-3/4 bg-muted/40 animate-pulse rounded" />
            <div className="h-4 w-1/2 bg-muted/40 animate-pulse rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <p className="text-muted-foreground text-center py-8">
        More campaigns coming soon.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {posts.slice(0, 3).map((post, i) => {
        const dateStr = post.date
          ? new Date(post.date).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "";
        return (
          <article
            key={post.id}
            data-index={i}
            className={`group ${i === 1 ? "md:mt-8 lg:mt-12" : ""} ${
              i === 2 ? "lg:mt-4" : ""
            }`}
          >
            <Link to={post.link || "#"} className="block">
              {post.featuredImage && (
                <div className="overflow-hidden rounded-lg bg-muted mb-5 aspect-[4/3] shadow-md">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              )}

              {post.categories && post.categories.length > 0 && (
                <p className="text-xs uppercase tracking-widest text-primary font-bold mb-2">
                  {post.categories[0].name}
                </p>
              )}

              <h3
                className="font-default text-xl sm:text-2xl leading-tight text-foreground group-hover:text-primary transition-colors mb-3 font-bold"
                dangerouslySetInnerHTML={{ __html: post.title }}
              />

              {dateStr && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Calendar className="size-3.5" />
                  {dateStr}
                </p>
              )}
            </Link>
          </article>
        );
      })}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  const relatedQuery = new WP_Query({
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc",
  });

  return (
    <section data-section-id="3406"
      id="single-post-template-section"
      className="bg-background text-foreground py-16 sm:py-20 lg:py-28"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="mt-24 lg:mt-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-7 lg:col-start-2">
              <p className="text-xs uppercase tracking-widest text-primary font-bold mb-4">
                Explore More
              </p>
              <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-foreground font-bold">
                More Campaigns from Vivid Studio
              </h2>
            </div>
            <div className="lg:col-span-3 lg:col-start-10 flex lg:items-end lg:justify-end">
              <Link to="/OurWorkPage">
                <Button variant="outline" className="rounded-lg border-primary text-primary hover:bg-primary hover:text-white">
                  View All Work
                  <ArrowRight className="size-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          <PostsProvider wp_query={{ post_type: "post", per_page: 3, orderby: "date", order: "desc" }}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}